import type { Condition, ConditionGroup } from "building-types/conditions/conditionTypes";
import type { Formula } from "building-types/options/formula";
import type { FormulaDisabled } from "building-types/options/formulaDisabled";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { DocOptionSet } from "building-types/options/optionSet";
import type { Table } from "common/data/table";
import deepequal from "common/utils/deepequal";
import { optionSetDefaultOptionId } from "doc/options/optionSetBaseUtils";
import type { ConditionTypeDef } from "./formulaConditions";
import { compileFormula } from "./formulaConditions";
import { formulaNew } from "./formulaNew";
import { formulaGetIdentifiers, isFormula, parseFormula } from "./formulaParsing";
import { checkFormula } from "./formulaUtils";

interface EvaluateFormulaParams {
  readonly conditions: Condition[];
  readonly conditionGroups: Table<ConditionGroup>;
  readonly typeDefs: Table<ConditionTypeDef>;
  readonly identifierPrefix?: string;
  readonly onCommitFormula: (formula: Formula | undefined) => void;
}

export function evaluateFormula({
  conditions,
  conditionGroups,
  typeDefs,
  identifierPrefix = "",
  onCommitFormula,
}: EvaluateFormulaParams) {
  const formula = compileFormula({
    conditions,
    conditionGroups,
    typeDefs,
    identifierPrefix: identifierPrefix,
  });

  if (!formula) {
    onCommitFormula(undefined);
    return;
  }

  const negation = `not (${formula || "true"})`;
  const { ok, errorMessage } = checkFormula(negation);
  if (errorMessage) {
    console.error("Invalid formula: ", { formula: negation, errorMessage });
  }
  if (ok) {
    onCommitFormula(formulaNew(negation));
  }
}

export function formulaFindMatchingDisabledField<T extends FormulaDisabled>(
  entities: T[],
): FormulaDisabled["disabled"] | undefined {
  const disabledFieldIdentical = entities.every(
    (entity) => isFormula(entity.disabled) && deepequal(entity.disabled, entities[0].disabled),
  );

  if (disabledFieldIdentical) {
    return entities[0].disabled;
  }
  return undefined;
}

// Use this function on unevaluated formulas to determine if they are disabled -- e.g. in the UI.
// During building generation the formulas are evaluated already.
export function isFormulaDisabledByOptionChoice(
  optionChoices: OptionChoices,
  optionSets: Table<DocOptionSet>,
  formulaDisabled: FormulaDisabled,
): boolean {
  if (formulaDisabled.disabled === undefined) {
    return false;
  }
  if (!isFormula(formulaDisabled.disabled)) {
    return formulaDisabled.disabled;
  }
  const vars = formulaGetIdentifiers(formulaDisabled.disabled);
  const resolvedOptionChoices = {};
  for (const varName of vars) {
    const choice = optionChoices[varName];
    resolvedOptionChoices[varName] = choice ?? optionSetDefaultOptionId(optionSets?.[varName], optionSets) ?? null;
  }

  return parseFormula(formulaDisabled.disabled.formula).evaluate(resolvedOptionChoices as any);
}
